<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue';

import CheckSolid from '@/components/svg/CheckSolid.vue';

type Props = {
  type: 'checkbox' | 'radio';
  checked: boolean;
  indeterminate?: boolean;
  ariaLabel?: string;
  screenReaderText?: string;
}

const props = withDefaults(defineProps<Props>(), {
  indeterminate: false,
});

const emit = defineEmits<{
  'change': [checked: boolean, event: Event];
}>();

const inputRef = ref<HTMLInputElement>();

const checked = computed(() => props.checked ? true : undefined);
const indeterminate = computed(() => props.indeterminate ? true : undefined);

function handleChange(event: Event) {
  const target = event.target as HTMLInputElement;
  emit('change', target.checked, event);
}

watch([() => props.indeterminate], async () => {
  if (inputRef.value) {
    await nextTick();
    inputRef.value.indeterminate = props.indeterminate;
  }
});
</script>

<template>
  <label class="selectLabel"
         :class="{
           'selectLabel_radio': type === 'radio',
           'selectLabel_checkbox': type === 'checkbox',
           'selectLabel_checked': checked,
           'selectLabel_indeterminate': indeterminate
         }"
         @click.stop="">
    <input ref="inputRef"
           :type="type"
           class="selectLabel-input"
           :checked="checked"
           :aria-label="ariaLabel"
           @change="handleChange" />
    <span class="selectLabel-iconWrapper"
          :hidden="!checked ? true : undefined">
      <CheckSolid class="selectLabel-icon"></CheckSolid>
    </span>
    <span class="selectLabel-iconWrapper"
          :hidden="!indeterminate ? true : undefined">
      <span :hidden="!indeterminate ? true : undefined">−</span>
    </span>
    <span v-if="screenReaderText"
          class="sr-only">
      {{ screenReaderText }}
    </span>
  </label>
</template>

<style lang="scss">
.selectLabel {
  --labelBackgroundColor: var(--gray-500);
  display: flex;
  align-items: center;
  justify-content: center;
  width: var(--selectionIconSize, 24px);
  height: var(--selectionIconSize, 24px);
  border: 2px solid var(--gray-300);
  margin-bottom: 0;

  cursor: pointer;
  transition-property: background-color, border-color;
  transition-duration: 0.15s;
  transition-timing-function: ease-in-out;
  position: relative;

  svg {
    width: 75%;
    height: 75%;
  }

  &:hover {
    background-color: oklch(from var(--labelBackgroundColor) l c h / .8);
  }

  &:focus-within {
    outline: 2px solid var(--primary-500);
    outline-offset: 2px;
  }

  &_checked {
    border-color: var(--clrGreenStatus, #22c55e);
    background-color: transparent;
  }

  &_indeterminate {
    border-color: var(--clrGreenStatus, #22c55e);
    background-color: var(--clrGreenStatus, #22c55e);
  }

  &_radio {
    border-radius: 50%;
  }

  &_checkbox {
    border-radius: 3px;
  }
}

.selectLabel-input {
  position: absolute;
  opacity: 0;
  scale: 0;
}

.selectLabel-iconWrapper {
  align-items: center;
  justify-content: center;
  height: calc(var(--selectionIconSize, 24px) - 6px);
  width: calc(var(--selectionIconSize, 24px) - 6px);
  background-color: var(--green-600);
  
  border-radius: inherit;
  
  color: var(--white);
  font-weight: bold;
  font-size: 14px;
  line-height: 1;

  transition-property: scale, display;
  transition-duration: 0.2s;
  transition-timing-function: ease-out;
  transition-behavior: allow-discrete;
  
  scale: 0;

  &:not([hidden]) {
    display: flex;
    scale: 1;

    @starting-style {
      scale: 0;
    }
  }
}
</style>
